import { SHORT_MONTHS } from "../../common/constant/dates";
import { C } from "../../common/theme/colors";
import { SalesItem, SalesOrder } from "../../common/requests/sales/endpoints";

/* ── statuses ────────────────────────────────────────────────────── */

/** Lifecycle order, used for sorting, the pipeline and the status filter. */
export const STATUS_ORDER = [
  "Pending Confirmation",
  "Pending Delivery",
  "In Delivery",
  "Completed",
  "Partially Refunded",
  "Refunded",
] as const;

/** Mirrors COMPLETABLE_STATUSES in admin-backend orders/base.py. */
export const COMPLETABLE_STATUSES = ["Pending Confirmation", "Pending Delivery", "In Delivery"];

/** Mirrors REFUNDABLE_STATUSES in admin-backend orders/base.py. */
export const REFUNDABLE_STATUSES = [...COMPLETABLE_STATUSES, "Completed", "Partially Refunded"];

/** The same solid colours as the status pills (StatusPill / statusTone).
 * Always rendered next to a text label. */
export const STATUS_COLORS: Record<string, string> = {
  "Pending Confirmation": "#6B625B",
  "Pending Delivery": C.ink,
  "In Delivery": C.moss,
  Completed: "#2E7D4F",
  "Partially Refunded": C.coralText,
  Refunded: C.danger,
};

export const statusRank = (status: string) => {
  const idx = (STATUS_ORDER as readonly string[]).indexOf(status);
  return idx === -1 ? STATUS_ORDER.length : idx;
};

/* ── money ───────────────────────────────────────────────────────── */

export { formatMoney, formatCount, formatPercent } from "../../common/utils/format";

/* ── lines: one row per order item, carrying its order ──────────── */

export interface SaleLine extends SalesItem {
  order: SalesOrder;
  createdAt: Date;
}

export function flattenOrders(orders: SalesOrder[]): SaleLine[] {
  return orders.flatMap((order) => {
    const createdAt = new Date(order.created_at);
    return order.items.map((item) => ({ ...item, order, createdAt }));
  });
}

/* ── date ranges ─────────────────────────────────────────────────── */

export type RangeKey = "7d" | "30d" | "90d" | "mtd" | "last_month" | "ytd" | "12m" | "all" | "custom";

export const RANGE_OPTIONS: { key: RangeKey; label: string }[] = [
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "90d", label: "Last 90 days" },
  { key: "mtd", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "ytd", label: "This year" },
  { key: "12m", label: "Last 12 months" },
  { key: "all", label: "All time" },
  { key: "custom", label: "Custom" },
];

export interface ResolvedRange {
  /** null = all time */
  start: Date | null;
  /** exclusive */
  end: Date;
  /** null when there is nothing meaningful to compare against */
  previous: { start: Date; end: Date } | null;
  label: string;
}

const DAY = 24 * 60 * 60 * 1000;

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const addMonths = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth() + n, d.getDate());

export const toDateInput = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export const parseDateInput = (value: string | undefined): Date | null => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const lastDays = (today: Date, days: number): Pick<ResolvedRange, "start" | "end" | "previous"> => {
  const end = addDays(today, 1);
  const start = addDays(end, -days);
  return { start, end, previous: { start: addDays(start, -days), end: start } };
};

/** Local-time calendar boundaries; the comparison period is the same length
 * immediately before (or, for month/year-to-date, the same days last period). */
export function resolveRange(key: RangeKey, from: string | undefined, to: string | undefined, now = new Date()): ResolvedRange {
  const today = startOfDay(now);
  const tomorrow = addDays(today, 1);
  const label = RANGE_OPTIONS.find((r) => r.key === key)?.label ?? "";
  switch (key) {
    case "7d":
      return { ...lastDays(today, 7), label };
    case "90d":
      return { ...lastDays(today, 90), label };
    case "12m":
      return { ...lastDays(today, 365), label };
    case "mtd": {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const prevStart = addMonths(start, -1);
      const elapsed = Math.round((tomorrow.getTime() - start.getTime()) / DAY);
      const prevEnd = new Date(Math.min(addDays(prevStart, elapsed).getTime(), start.getTime()));
      return { start, end: tomorrow, previous: { start: prevStart, end: prevEnd }, label };
    }
    case "last_month": {
      const end = new Date(today.getFullYear(), today.getMonth(), 1);
      const start = addMonths(end, -1);
      return { start, end, previous: { start: addMonths(start, -1), end: start }, label };
    }
    case "ytd": {
      const start = new Date(today.getFullYear(), 0, 1);
      const prevStart = new Date(today.getFullYear() - 1, 0, 1);
      return {
        start,
        end: tomorrow,
        previous: { start: prevStart, end: new Date(today.getFullYear() - 1, today.getMonth(), today.getDate() + 1) },
        label,
      };
    }
    case "all":
      return { start: null, end: tomorrow, previous: null, label };
    case "custom": {
      const fromDate = parseDateInput(from) ?? addDays(today, -29);
      const toDate = parseDateInput(to) ?? today;
      const start = fromDate <= toDate ? fromDate : toDate;
      const end = addDays(fromDate <= toDate ? toDate : fromDate, 1);
      const days = Math.round((end.getTime() - start.getTime()) / DAY);
      return {
        start,
        end,
        previous: { start: addDays(start, -days), end: start },
        label: `${formatDate(start)} – ${formatDate(addDays(end, -1))}`,
      };
    }
    case "30d":
    default:
      return { ...lastDays(today, 30), label: "Last 30 days" };
  }
}

export const inRange = (line: SaleLine, start: Date | null, end: Date) =>
  (start === null || line.createdAt >= start) && line.createdAt < end;

/* ── filters ─────────────────────────────────────────────────────── */

export interface SalesFilterState {
  range: RangeKey;
  from?: string;
  to?: string;
  statuses: string[];
  sellers: string[]; // seller_key
  buyers: string[]; // buyer_key
  currencies: string[];
  countries: string[];
  genera: string[];
  q: string;
  attention: boolean;
}

export const DEFAULT_FILTERS: SalesFilterState = {
  range: "all",
  statuses: [],
  sellers: [],
  buyers: [],
  currencies: [],
  countries: [],
  genera: [],
  q: "",
  attention: false,
};

const LIST_KEYS = ["statuses", "sellers", "buyers", "currencies", "countries", "genera"] as const;

export function filtersFromParams(params: URLSearchParams): SalesFilterState {
  const range = (params.get("range") as RangeKey) || DEFAULT_FILTERS.range;
  const state: SalesFilterState = {
    ...DEFAULT_FILTERS,
    range: RANGE_OPTIONS.some((r) => r.key === range) ? range : DEFAULT_FILTERS.range,
    from: params.get("from") ?? undefined,
    to: params.get("to") ?? undefined,
    q: params.get("q") ?? "",
    attention: params.get("attention") === "1",
  };
  LIST_KEYS.forEach((key) => {
    const raw = params.get(key);
    state[key] = raw ? raw.split(",").filter(Boolean) : [];
  });
  return state;
}

export function filtersToParams(state: SalesFilterState): URLSearchParams {
  const params = new URLSearchParams();
  if (state.range !== DEFAULT_FILTERS.range) params.set("range", state.range);
  if (state.range === "custom") {
    if (state.from) params.set("from", state.from);
    if (state.to) params.set("to", state.to);
  }
  LIST_KEYS.forEach((key) => {
    if (state[key].length) params.set(key, state[key].join(","));
  });
  if (state.q.trim()) params.set("q", state.q.trim());
  if (state.attention) params.set("attention", "1");
  return params;
}

export const activeFilterCount = (s: SalesFilterState) =>
  LIST_KEYS.reduce((n, key) => n + (s[key].length ? 1 : 0), 0) + (s.q.trim() ? 1 : 0) + (s.attention ? 1 : 0);

const matchesQuery = (line: SaleLine, q: string) => {
  if (!q) return true;
  const haystack = [
    line.order.order_id,
    line.name,
    line.seller,
    line.order.buyer,
    line.tracking_number,
    line.genus,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
};

/** Everything except date range and (optionally) status. Status is left out
 * for the pipeline so it can act as the status picker. */
export function applyDimensionFilters(lines: SaleLine[], f: SalesFilterState, opts: { skipStatus?: boolean } = {}) {
  const q = f.q.trim().toLowerCase();
  return lines.filter(
    (l) =>
      (opts.skipStatus || !f.statuses.length || f.statuses.includes(l.status)) &&
      (!f.sellers.length || (l.seller_key !== null && f.sellers.includes(l.seller_key))) &&
      (!f.buyers.length || (l.order.buyer_key !== null && f.buyers.includes(l.order.buyer_key))) &&
      (!f.currencies.length || f.currencies.includes(l.order.currency)) &&
      (!f.countries.length || f.countries.includes(l.order.buyer_country ?? "Unknown")) &&
      (!f.genera.length || f.genera.includes(l.genus ?? "Unknown")) &&
      matchesQuery(l, q)
  );
}

/* ── needs attention ─────────────────────────────────────────────── */

export const ATTENTION_RULES = {
  unconfirmedDays: 3,
  unshippedDays: 5,
  inDeliveryDays: 14,
};

export const daysSince = (iso: string | Date | null, now = new Date()) => {
  if (!iso) return null;
  const t = typeof iso === "string" ? new Date(iso) : iso;
  return Math.floor((now.getTime() - t.getTime()) / DAY);
};

/** Why an item needs a human, or null. Ages count from when the order was
 * paid (the marketplace doesn't timestamp status changes), except In
 * Delivery, which counts from when the item was marked shipped. */
export function attentionReason(line: SaleLine, now = new Date()): string | null {
  const age = daysSince(line.createdAt, now) ?? 0;
  switch (line.status) {
    case "Pending Confirmation":
      return age >= ATTENTION_RULES.unconfirmedDays ? `Seller hasn't confirmed after ${age}d` : null;
    case "Pending Delivery":
      return age >= ATTENTION_RULES.unshippedDays ? `Not shipped ${age}d after payment` : null;
    case "In Delivery": {
      if (!line.tracking_number) return "In delivery without a tracking number";
      const shippedAge = daysSince(line.shipped_at ?? line.tracking_added_at, now) ?? age;
      return shippedAge >= ATTENTION_RULES.inDeliveryDays ? `In delivery for ${shippedAge}d` : null;
    }
    default:
      return null;
  }
}

/* ── summaries ───────────────────────────────────────────────────── */

export interface Summary {
  gmv: number;
  platformRevenue: number;
  takeRate: number | null;
  orders: number;
  aov: number | null;
  buyers: number;
  newBuyers: number;
  returningBuyers: number;
  repeatBuyerRate: number | null;
  sellers: number;
  units: number;
  items: number;
  discounts: number;
  refundedValue: number;
  refundedItems: number;
  specialOfferShare: number | null;
  fxMissingOrders: number;
  firstSale: Date | null;
}

export function summarise(lines: SaleLine[]): Summary {
  const revenueLines = lines.filter((l) => l.counts_toward_revenue);
  const sum = (ls: SaleLine[], pick: (l: SaleLine) => number | null) =>
    ls.reduce((acc, l) => acc + (pick(l) ?? 0), 0);

  const gmv = sum(revenueLines, (l) => l.net_reporting);
  const platformRevenue = sum(revenueLines, (l) => l.platform_fee_reporting);
  const ordersByBuyer = new Map<string, Set<string>>();
  const newBuyers = new Set<string>();
  const orders = new Set<string>();
  const sellers = new Set<string>();
  revenueLines.forEach((l) => {
    orders.add(l.order.order_id);
    if (l.seller_key) sellers.add(l.seller_key);
    const buyer = l.order.buyer_key;
    if (!buyer) return;
    if (!ordersByBuyer.has(buyer)) ordersByBuyer.set(buyer, new Set());
    ordersByBuyer.get(buyer)!.add(l.order.order_id);
    if (l.order.is_first_order) newBuyers.add(buyer);
  });
  const buyers = ordersByBuyer.size;
  const repeatBuyers = Array.from(ordersByBuyer.values()).filter((s) => s.size > 1).length;
  const refunded = lines.filter((l) => !l.counts_toward_revenue);
  const specialOfferValue = sum(revenueLines.filter((l) => l.is_special_offer), (l) => l.net_reporting);

  return {
    gmv,
    platformRevenue,
    takeRate: gmv > 0 ? platformRevenue / gmv : null,
    orders: orders.size,
    aov: orders.size ? gmv / orders.size : null,
    buyers,
    newBuyers: newBuyers.size,
    returningBuyers: buyers - newBuyers.size,
    repeatBuyerRate: buyers ? repeatBuyers / buyers : null,
    sellers: sellers.size,
    units: sum(revenueLines, (l) => l.quantity),
    items: revenueLines.length,
    discounts: sum(revenueLines, (l) => l.discount_reporting),
    refundedValue: sum(refunded, (l) => l.net_reporting),
    refundedItems: refunded.length,
    specialOfferShare: gmv > 0 ? specialOfferValue / gmv : null,
    fxMissingOrders: new Set(lines.filter((l) => l.order.fx_missing).map((l) => l.order.order_id)).size,
    firstSale: lines.length ? new Date(Math.min(...lines.map((l) => l.createdAt.getTime()))) : null,
  };
}

/** Relative change, or null when there's no base to compare with. */
export const change = (current: number | null, previous: number | null) =>
  current === null || previous === null || previous === 0 ? null : (current - previous) / Math.abs(previous);

/* ── time series ─────────────────────────────────────────────────── */

export type Granularity = "day" | "week" | "month";
export type TrendMetric = "gmv" | "orders" | "platformRevenue" | "units";

export const TREND_METRICS: { key: TrendMetric; label: string; money: boolean }[] = [
  { key: "gmv", label: "GMV", money: true },
  { key: "platformRevenue", label: "Platform revenue", money: true },
  { key: "orders", label: "Orders", money: false },
  { key: "units", label: "Units", money: false },
];

export function autoGranularity(start: Date, end: Date): Granularity {
  const days = (end.getTime() - start.getTime()) / DAY;
  if (days <= 31) return "day";
  if (days <= 180) return "week";
  return "month";
}

const bucketStart = (d: Date, g: Granularity) => {
  if (g === "month") return new Date(d.getFullYear(), d.getMonth(), 1);
  const day = startOfDay(d);
  if (g === "day") return day;
  const mondayOffset = (day.getDay() + 6) % 7;
  return addDays(day, -mondayOffset);
};
const nextBucket = (d: Date, g: Granularity) =>
  g === "month" ? new Date(d.getFullYear(), d.getMonth() + 1, 1) : addDays(d, g === "week" ? 7 : 1);

// Fixed names: en-GB's "short" month is "Sept" for September but three
// letters for every other month, which made axis labels uneven.
const MONTHS = SHORT_MONTHS;

/** "26 Sep 2026" — used for every date on the page so months are uniform. */
export const formatDate = (d: Date) => `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;

/** Short form for axis ticks, long form for tooltips. Months carry an
 * apostrophe year ("Sep '26") so they never read as a day ("26 Sep"). */
export function formatBucket(d: Date, g: Granularity, long = false): string {
  const month = MONTHS[d.getMonth()];
  if (g === "month") return long ? `${month} ${d.getFullYear()}` : `${month} '${String(d.getFullYear()).slice(-2)}`;
  const day = `${d.getDate()} ${month}`;
  if (!long) return day;
  return g === "week" ? `Week of ${day} ${d.getFullYear()}` : `${day} ${d.getFullYear()}`;
}

/** Every n-th label, so ticks are evenly spaced. Steps snap to calendar-
 * friendly sizes (weekly for days, quarterly for months). */
export function labelStride(count: number, plotWidth: number, g: Granularity, minLabelWidth = 52): number {
  const fit = Math.max(1, Math.floor(plotWidth / minLabelWidth));
  if (count <= fit) return 1;
  const steps = g === "day" ? [1, 2, 7, 14, 28] : g === "week" ? [1, 2, 4, 8, 13, 26] : [1, 2, 3, 6, 12, 24];
  const needed = Math.ceil(count / fit);
  return steps.find((s) => s >= needed) ?? needed;
}

export interface TrendPoint {
  key: string;
  label: string;
  longLabel: string;
  start: Date;
  value: number;
  partial: boolean;
}

export function buildTrend(
  lines: SaleLine[],
  start: Date,
  end: Date,
  g: Granularity,
  metric: TrendMetric,
  cumulative: boolean,
  now = new Date()
): TrendPoint[] {
  const points: TrendPoint[] = [];
  const index = new Map<number, TrendPoint>();
  for (let b = bucketStart(start, g); b < end; b = nextBucket(b, g)) {
    const point = {
      key: b.toISOString(),
      label: formatBucket(b, g),
      longLabel: formatBucket(b, g, true),
      start: b,
      value: 0,
      partial: nextBucket(b, g) > now,
    };
    points.push(point);
    index.set(b.getTime(), point);
  }
  const ordersSeen = new Map<number, Set<string>>();
  lines.forEach((l) => {
    if (!l.counts_toward_revenue || l.createdAt < start || l.createdAt >= end) return;
    const key = bucketStart(l.createdAt, g).getTime();
    const point = index.get(key);
    if (!point) return;
    if (metric === "gmv") point.value += l.net_reporting ?? 0;
    else if (metric === "platformRevenue") point.value += l.platform_fee_reporting ?? 0;
    else if (metric === "units") point.value += l.quantity;
    else {
      if (!ordersSeen.has(key)) ordersSeen.set(key, new Set());
      ordersSeen.get(key)!.add(l.order.order_id);
      point.value = ordersSeen.get(key)!.size;
    }
  });
  if (cumulative) {
    let running = 0;
    points.forEach((p) => {
      running += p.value;
      p.value = running;
    });
  }
  return points;
}

/* ── breakdowns ──────────────────────────────────────────────────── */

export interface RankedRow {
  key: string;
  label: string;
  value: number;
  orders: number;
}

export function rankBy(
  lines: SaleLine[],
  keyOf: (l: SaleLine) => string | null,
  labelOf: (l: SaleLine) => string
): RankedRow[] {
  const rows = new Map<string, RankedRow & { orderIds: Set<string> }>();
  lines.forEach((l) => {
    if (!l.counts_toward_revenue) return;
    const key = keyOf(l);
    if (key === null) return;
    if (!rows.has(key)) rows.set(key, { key, label: labelOf(l), value: 0, orders: 0, orderIds: new Set() });
    const row = rows.get(key)!;
    row.value += l.net_reporting ?? 0;
    row.orderIds.add(l.order.order_id);
  });
  return Array.from(rows.values())
    .map(({ orderIds, ...row }) => ({ ...row, orders: orderIds.size }))
    .sort((a, b) => b.value - a.value);
}

/* ── pipeline ────────────────────────────────────────────────────── */

export interface PipelineStage {
  status: string;
  items: number;
  orders: number;
  value: number;
  /** How long the oldest item in this (open) stage has been waiting, in ms */
  oldestAgeMs: number | null;
}

export function buildPipeline(lines: SaleLine[], now = new Date()): PipelineStage[] {
  const known = STATUS_ORDER as readonly string[];
  const extra = Array.from(new Set(lines.map((l) => l.status))).filter((s) => !known.includes(s));
  return [...known, ...extra].map((status) => {
    const ls = lines.filter((l) => l.status === status);
    const open = COMPLETABLE_STATUSES.includes(status);
    return {
      status,
      items: ls.length,
      orders: new Set(ls.map((l) => l.order.order_id)).size,
      value: ls.reduce((acc, l) => acc + (l.net_reporting ?? 0), 0),
      oldestAgeMs: open && ls.length ? Math.max(...ls.map((l) => now.getTime() - l.createdAt.getTime())) : null,
    };
  });
}

/* ── grouping for the table ──────────────────────────────────────── */

export interface OrderGroup {
  order: SalesOrder;
  createdAt: Date;
  lines: SaleLine[];
  /** Totals of the visible lines (respecting filters) */
  net: number;
  netReporting: number | null;
  statuses: string[];
  attention: string[];
  completable: boolean;
  /** Has a paid item that isn't refunded yet (Cancel applies to the whole order) */
  refundable: boolean;
}

export function groupByOrder(lines: SaleLine[], now = new Date()): OrderGroup[] {
  const groups = new Map<string, OrderGroup>();
  lines.forEach((l) => {
    let g = groups.get(l.order.order_id);
    if (!g) {
      g = {
        order: l.order,
        createdAt: l.createdAt,
        lines: [],
        net: 0,
        netReporting: 0,
        statuses: [],
        attention: [],
        // Decided from the whole order, not the filtered lines: completing acts on every item.
        completable: l.order.items.some((i) => COMPLETABLE_STATUSES.includes(i.status)),
        refundable: l.order.items.some((i) => REFUNDABLE_STATUSES.includes(i.status)),
      };
      groups.set(l.order.order_id, g);
    }
    g.lines.push(l);
    if (l.counts_toward_revenue) {
      g.net += l.net;
      g.netReporting = g.netReporting === null || l.net_reporting === null ? null : g.netReporting + l.net_reporting;
    }
    if (!g.statuses.includes(l.status)) g.statuses.push(l.status);
    const reason = attentionReason(l, now);
    if (reason && !g.attention.includes(reason)) g.attention.push(reason);
  });
  groups.forEach((g) => g.statuses.sort((a, b) => statusRank(a) - statusRank(b)));
  return Array.from(groups.values());
}

/* ── export ──────────────────────────────────────────────────────── */

const csvCell = (value: unknown) => {
  if (value === null || value === undefined) return "";
  const s = String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function linesToCsv(lines: SaleLine[], reportingCurrency: string, includeIdentity: boolean): string {
  const header = [
    "order_id",
    "placed_at",
    ...(includeIdentity ? ["buyer"] : []),
    "buyer_country",
    "new_buyer",
    ...(includeIdentity ? ["seller"] : []),
    "listing",
    "genus",
    "status",
    "special_offer",
    "currency",
    "quantity",
    "unit_price",
    "delivery",
    "discount",
    "net",
    `net_${reportingCurrency}`,
    `platform_fee_${reportingCurrency}`,
    "counts_toward_revenue",
    "delivery_provider",
    "tracking_number",
  ];
  const rows = lines.map((l) => [
    l.order.order_id,
    l.order.created_at,
    ...(includeIdentity ? [l.order.buyer] : []),
    l.order.buyer_country,
    l.order.is_first_order,
    ...(includeIdentity ? [l.seller] : []),
    l.name,
    l.genus,
    l.status,
    l.is_special_offer,
    l.order.currency,
    l.quantity,
    l.unit_price,
    l.delivery,
    l.discount,
    l.net,
    l.net_reporting,
    l.platform_fee_reporting,
    l.counts_toward_revenue,
    l.delivery_provider,
    l.tracking_number,
  ]);
  return [header, ...rows].map((r) => r.map(csvCell).join(",")).join("\n");
}

export function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export const trackingUrl = (tracking: string) =>
  `https://parcelsapp.com/en/tracking/${encodeURIComponent(tracking.trim())}`;
